import { useEffect, useRef } from 'react';
import { Channel } from '@/models';
import { FilterState } from '@/types/filter';
import { useLocalFiltersRegistry } from '@/context/LocalFiltersRegistry/LocalFiltersRegistry';
import { LOCAL_FILTERS_KEY_CHANNEL_FOCUS_SECONDARY } from '@/context/LocalFiltersRegistry/localFiltersKeys';
import { isRecord } from '@/utils/filter/isRecord';
import { useChannelFocusStore } from '@/pages/ChannelFocus/store/useChannelFocusStore';
import {
  readSecondarySegmentFilters,
  writeSecondarySegmentFilters,
  type SecondarySegmentFiltersState,
} from '@/pages/ChannelFocus/store/secondarySegmentFilterStorage';

type ChannelNestedFilters = Record<string, FilterState>;

const toSecondarySegmentState = (filters: ChannelNestedFilters): SecondarySegmentFiltersState => {
  const result: SecondarySegmentFiltersState = {};
  for (const channel of Object.values(Channel)) {
    const channelFilters = filters[channel];
    if (isRecord(channelFilters)) {
      result[channel] = channelFilters;
    }
  }
  return result;
};

/**
 * Registers the Channel Focus secondary segment (comparison) filters with the
 * LocalFiltersRegistry so they participate in saved filter sets and shared
 * views. Mirrors the channel-nested shape used by the additional filters.
 */
export const useRegisterSecondarySegmentFilters = (channel: Channel): void => {
  const { register, unregister } = useLocalFiltersRegistry();
  const setInteractionFilter = useChannelFocusStore(state => state.setInteractionFilter);
  const channelRef = useRef(channel);
  channelRef.current = channel;

  useEffect(() => {
    register<ChannelNestedFilters>(LOCAL_FILTERS_KEY_CHANNEL_FOCUS_SECONDARY, {
      getFilters: () => {
        const currentChannel = channelRef.current;
        const filtersForChannel = readSecondarySegmentFilters()[currentChannel];
        if (!filtersForChannel || Object.keys(filtersForChannel).length === 0) return {};
        return { [currentChannel]: filtersForChannel };
      },
      setFilters: (filters: ChannelNestedFilters) => {
        const nextState = toSecondarySegmentState(filters);
        writeSecondarySegmentFilters(nextState);
        const currentChannel = channelRef.current;
        setInteractionFilter(nextState[currentChannel] ?? {});
      },
    });
    return () => {
      unregister(LOCAL_FILTERS_KEY_CHANNEL_FOCUS_SECONDARY);
    };
  }, [register, unregister, setInteractionFilter]);
};
